import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Plus,
  Trash2,
  FileText,
  DollarSign
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import vendorInvoiceService from '../../services/vendorInvoiceService'
import purchaseOrderService from '../../services/purchaseOrderService'
import goodsReceiptNoteService from '../../services/goodsReceiptNoteService'
import '../../styles/expenseClaimForm.css'

const VendorInvoiceForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEdit = location.pathname.includes('/edit')
  const isView = location.pathname.includes('/view')
  const invoiceData = location.state?.invoiceData

  const [formData, setFormData] = useState({
    invoiceNumber: '',
    supplierInvoiceNumber: '',
    purchaseOrder: '',
    purchaseOrderNumber: '',
    goodsReceiptNote: '',
    grnNumber: '',
    supplier: '',
    supplierName: '',
    supplierCode: '',
    supplierAddress: {
      street: '',
      city: '',
      state: '',
      postalCode: '',
      country: 'India'
    },
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    invoiceItems: [],
    financialSummary: {
      subtotal: 0,
      totalDiscount: 0,
      totalTaxAmount: 0,
      totalCGST: 0,
      totalSGST: 0,
      totalIGST: 0,
      shippingCharges: 0,
      otherCharges: 0,
      roundOff: 0,
      totalAmount: 0,
      currency: 'INR'
    },
    taxInformation: {
      gstNumber: '',
      placeOfSupply: '',
      supplyType: 'Intrastate'
    },
    notes: '',
    internalNotes: ''
  })

  const [isLoadingInvoiceNumber, setIsLoadingInvoiceNumber] = useState(true)
  const [sourceType, setSourceType] = useState('') // 'po' or 'grn' or ''
  const [purchaseOrders, setPurchaseOrders] = useState([])
  const [grns, setGRNs] = useState([])
  const [loading, setLoading] = useState(false)

  // Load initial data
  useEffect(() => {
    if (!isEdit && !isView) {
      fetchInvoiceNumber()
    } else if (invoiceData) {
      loadInvoiceData(invoiceData._id)
    }

    // Load dropdown data
    loadDropdownData()
  }, [])

  // Calculate totals when items change
  useEffect(() => {
    if (formData.invoiceItems && formData.invoiceItems.length > 0) {
      calculateTotals()
    }
  }, [formData.invoiceItems, formData.financialSummary.shippingCharges, formData.financialSummary.otherCharges, formData.taxInformation.supplyType])

  const fetchInvoiceNumber = async () => {
    try {
      setIsLoadingInvoiceNumber(true)
      const response = await vendorInvoiceService.getNextInvoiceNumber()
      if (response.success && response.data) {
        setFormData(prev => ({
          ...prev,
          invoiceNumber: response.data.invoiceNumber || response.data.formattedValue || 'INV0001'
        }))
      }
    } catch (error) {
      console.error('Error fetching invoice number:', error)
      setFormData(prev => ({ ...prev, invoiceNumber: 'INV0001' }))
    } finally {
      setIsLoadingInvoiceNumber(false)
    }
  }

  const loadInvoiceData = async (id) => {
    try {
      setLoading(true)
      const response = await vendorInvoiceService.getVendorInvoiceById(id)
      if (response.success && response.data) {
        const invoice = response.data
        setFormData({
          invoiceNumber: invoice.invoiceNumber || '',
          supplierInvoiceNumber: invoice.supplierInvoiceNumber || '',
          purchaseOrder: invoice.purchaseOrder?._id || invoice.purchaseOrder || '',
          purchaseOrderNumber: invoice.purchaseOrderNumber || '',
          goodsReceiptNote: invoice.goodsReceiptNote?._id || invoice.goodsReceiptNote || '',
          grnNumber: invoice.grnNumber || '',
          supplier: invoice.supplier?._id || invoice.supplier || '',
          supplierName: invoice.supplierName || '',
          supplierCode: invoice.supplierCode || '',
          supplierAddress: invoice.supplierAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          },
          invoiceDate: invoice.invoiceDate ? new Date(invoice.invoiceDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : '',
          invoiceItems: invoice.invoiceItems || [],
          financialSummary: invoice.financialSummary || {
            subtotal: 0,
            totalDiscount: 0,
            totalTaxAmount: 0,
            totalCGST: 0,
            totalSGST: 0,
            totalIGST: 0,
            shippingCharges: 0,
            otherCharges: 0,
            roundOff: 0,
            totalAmount: 0,
            currency: 'INR'
          },
          taxInformation: invoice.taxInformation || {
            gstNumber: '',
            placeOfSupply: '',
            supplyType: 'Intrastate'
          },
          notes: invoice.notes || '',
          internalNotes: invoice.internalNotes || ''
        })
      }
    } catch (error) {
      console.error('Error loading invoice data:', error)
      toast.error('Failed to load invoice data')
    } finally {
      setLoading(false)
      setIsLoadingInvoiceNumber(false)
    }
  }

  const loadDropdownData = async () => {
    try {
      const [posRes, grnsRes] = await Promise.all([
        purchaseOrderService.getAllPurchaseOrders({ status: 'Received', limit: 1000 }),
        goodsReceiptNoteService.getAllGRNs({ status: 'Accepted', limit: 1000 })
      ])

      if (posRes.success) setPurchaseOrders(posRes.data || [])
      if (grnsRes.success) setGRNs(grnsRes.data || [])
    } catch (error) {
      console.error('Error loading dropdown data:', error)
    }
  }

  const calculateTotals = () => {
    let subtotal = 0
    let totalDiscount = 0
    let totalTaxAmount = 0
    let totalCGST = 0
    let totalSGST = 0
    let totalIGST = 0

    formData.invoiceItems.forEach(item => {
      const itemSubtotal = (item.unitPrice || 0) * (item.quantity || 0)
      const discountAmt = itemSubtotal * ((item.discount || 0) / 100)
      const afterDiscount = itemSubtotal - discountAmt
      const taxAmt = afterDiscount * ((item.taxRate || 0) / 100)
      
      // Calculate GST components
      if (formData.taxInformation.supplyType === 'Intrastate') {
        item.cgstAmount = taxAmt / 2
        item.sgstAmount = taxAmt / 2
        item.igstAmount = 0
        totalCGST += item.cgstAmount
        totalSGST += item.sgstAmount
      } else {
        item.cgstAmount = 0
        item.sgstAmount = 0
        item.igstAmount = taxAmt
        totalIGST += item.igstAmount
      }

      subtotal += itemSubtotal
      totalDiscount += discountAmt
      totalTaxAmount += taxAmt
    })

    const totalBeforeRoundOff = subtotal - totalDiscount + totalTaxAmount + 
      (formData.financialSummary.shippingCharges || 0) + 
      (formData.financialSummary.otherCharges || 0)
    
    const roundOff = Math.round(totalBeforeRoundOff) - totalBeforeRoundOff
    const totalAmount = Math.round(totalBeforeRoundOff)

    setFormData(prev => ({
      ...prev,
      financialSummary: {
        ...prev.financialSummary,
        subtotal,
        totalDiscount,
        totalTaxAmount,
        totalCGST,
        totalSGST,
        totalIGST,
        roundOff,
        totalAmount
      }
    }))
  }

  const handleInputChange = (e) => {
    const { name, value, type } = e.target
    
    if (name.startsWith('supplierAddress.')) {
      const addressField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        supplierAddress: {
          ...prev.supplierAddress,
          [addressField]: value
        }
      }))
    } else if (name.startsWith('financialSummary.')) {
      const financialField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        financialSummary: {
          ...prev.financialSummary,
          [financialField]: type === 'number' ? parseFloat(value) || 0 : value
        }
      }))
    } else if (name.startsWith('taxInformation.')) {
      const taxField = name.split('.')[1]
      setFormData(prev => ({
        ...prev,
        taxInformation: {
          ...prev.taxInformation,
          [taxField]: value
        }
      }))
    } else if (name === 'sourceType') {
      setSourceType(value)
      // Clear source document when type changes
      setFormData(prev => ({
        ...prev,
        purchaseOrder: '',
        purchaseOrderNumber: '',
        goodsReceiptNote: '',
        grnNumber: '',
        invoiceItems: []
      }))
    } else if (name === 'sourceDocument') {
      if (sourceType === 'po') {
        loadPurchaseOrderData(value)
      } else if (sourceType === 'grn') {
        loadGRNData(value)
      }
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }
  }

  const loadPurchaseOrderData = async (purchaseOrderId) => {
    if (!purchaseOrderId) return

    try {
      const response = await vendorInvoiceService.getSourceDocumentForInvoice('po', purchaseOrderId)
      if (response.success && response.data) {
        const po = response.data
        setFormData(prev => ({
          ...prev,
          purchaseOrder: purchaseOrderId,
          purchaseOrderNumber: po.purchaseOrderNumber || '',
          supplier: po.supplier?._id || po.supplier || '',
          supplierName: po.supplierName || '',
          supplierCode: po.supplierCode || '',
          supplierAddress: po.supplierAddress || po.supplier?.billingAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          },
          invoiceItems: po.orderItems?.map((item, idx) => ({
            itemNumber: idx + 1,
            itemType: item.itemType || 'Product',
            product: item.product?._id || item.product || '',
            productName: item.productName || '',
            productCode: item.productCode || '',
            service: item.service?._id || item.service || '',
            serviceName: item.serviceName || '',
            serviceCode: item.serviceCode || '',
            category: item.category?._id || item.category || '',
            categoryName: item.categoryName || '',
            description: item.description || '',
            quantity: item.quantity || 0,
            unitOfMeasurement: item.unitOfMeasurement || 'Unit',
            unitPrice: item.unitPrice || 0,
            discount: item.discount || 0,
            discountAmount: 0,
            taxRate: item.taxRate || 0,
            taxAmount: 0,
            totalAmount: 0,
            currency: item.currency || 'INR',
            hsnCode: item.hsnCode || '',
            sacCode: item.sacCode || '',
            gstRate: item.gstRate || 0,
            cgstAmount: 0,
            sgstAmount: 0,
            igstAmount: 0
          })) || []
        }))
      }
    } catch (error) {
      console.error('Error loading purchase order data:', error)
      toast.error('Failed to load purchase order data')
    }
  }

  const loadGRNData = async (grnId) => {
    if (!grnId) return

    try {
      const response = await vendorInvoiceService.getSourceDocumentForInvoice('grn', grnId)
      if (response.success && response.data) {
        const grn = response.data
        setFormData(prev => ({
          ...prev,
          goodsReceiptNote: grnId,
          grnNumber: grn.grnNumber || '',
          purchaseOrder: grn.purchaseOrder?._id || grn.purchaseOrder || '',
          purchaseOrderNumber: grn.purchaseOrderNumber || '',
          supplier: grn.supplier?._id || grn.supplier || '',
          supplierName: grn.supplierName || '',
          supplierCode: grn.supplierCode || '',
          supplierAddress: grn.supplierAddress || grn.supplier?.billingAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: 'India'
          },
          invoiceItems: grn.receiptItems?.map((item, idx) => ({
            itemNumber: idx + 1,
            itemType: item.itemType || 'Product',
            product: item.product?._id || item.product || '',
            productName: item.productName || '',
            productCode: item.productCode || '',
            service: item.service?._id || item.service || '',
            serviceName: item.serviceName || '',
            serviceCode: item.serviceCode || '',
            category: item.category?._id || item.category || '',
            categoryName: item.categoryName || '',
            description: '',
            quantity: item.acceptedQuantity || item.receivedQuantity || 0,
            unitOfMeasurement: item.unitOfMeasurement || 'Unit',
            unitPrice: item.unitPrice || 0,
            discount: 0,
            discountAmount: 0,
            taxRate: 0,
            taxAmount: 0,
            totalAmount: 0,
            currency: item.currency || 'INR',
            hsnCode: '',
            sacCode: '',
            gstRate: 0,
            cgstAmount: 0,
            sgstAmount: 0,
            igstAmount: 0
          })) || []
        }))
      }
    } catch (error) {
      console.error('Error loading GRN data:', error)
      toast.error('Failed to load GRN data')
    }
  }

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.invoiceItems]
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: field === 'quantity' || field === 'unitPrice' || field === 'discount' || field === 'taxRate' || field === 'gstRate'
        ? parseFloat(value) || 0
        : value
    }

    // Calculate item totals
    const itemSubtotal = (updatedItems[index].unitPrice || 0) * (updatedItems[index].quantity || 0)
    const discountAmt = itemSubtotal * ((updatedItems[index].discount || 0) / 100)
    const afterDiscount = itemSubtotal - discountAmt
    const taxAmt = afterDiscount * ((updatedItems[index].taxRate || 0) / 100)
    
    updatedItems[index].discountAmount = discountAmt
    updatedItems[index].taxAmount = taxAmt
    updatedItems[index].totalAmount = afterDiscount + taxAmt

    // Calculate GST components
    if (formData.taxInformation.supplyType === 'Intrastate') {
      updatedItems[index].cgstAmount = taxAmt / 2
      updatedItems[index].sgstAmount = taxAmt / 2
      updatedItems[index].igstAmount = 0
    } else {
      updatedItems[index].cgstAmount = 0
      updatedItems[index].sgstAmount = 0
      updatedItems[index].igstAmount = taxAmt
    }

    setFormData(prev => ({
      ...prev,
      invoiceItems: updatedItems
    }))
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.supplierInvoiceNumber || !formData.supplier || !formData.dueDate) {
        toast.error('Please fill in all required fields (Supplier Invoice Number, Supplier, and Due Date)')
        return
      }

      if (!formData.invoiceItems || formData.invoiceItems.length === 0) {
        toast.error('Please add at least one invoice item')
        return
      }

      setLoading(true)

      if (isEdit && invoiceData?._id) {
        const response = await vendorInvoiceService.updateVendorInvoice(invoiceData._id, formData)
        if (response.success) {
          toast.success('Vendor invoice updated successfully!')
          navigate('/procurement/vendor-invoice')
        } else {
          toast.error(response.message || 'Failed to update vendor invoice')
        }
      } else {
        const response = await vendorInvoiceService.createVendorInvoice(formData)
        if (response.success) {
          toast.success('Vendor invoice created successfully!')
          navigate('/procurement/vendor-invoice')
        } else {
          toast.error(response.message || 'Failed to create vendor invoice')
        }
      }
    } catch (error) {
      console.error('Error saving vendor invoice:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save vendor invoice')
        }
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    if (isEdit || isView) {
      loadInvoiceData(invoiceData._id)
    } else {
      setFormData(prev => ({
        ...prev,
        supplierInvoiceNumber: '',
        purchaseOrder: '',
        purchaseOrderNumber: '',
        goodsReceiptNote: '',
        grnNumber: '',
        supplier: '',
        supplierName: '',
        supplierCode: '',
        supplierAddress: {
          street: '',
          city: '',
          state: '',
          postalCode: '',
          country: 'India'
        },
        dueDate: '',
        invoiceItems: [],
        financialSummary: {
          subtotal: 0,
          totalDiscount: 0,
          totalTaxAmount: 0,
          totalCGST: 0,
          totalSGST: 0,
          totalIGST: 0,
          shippingCharges: 0,
          otherCharges: 0,
          roundOff: 0,
          totalAmount: 0,
          currency: 'INR'
        },
        taxInformation: {
          gstNumber: '',
          placeOfSupply: '',
          supplyType: 'Intrastate'
        },
        notes: '',
        internalNotes: ''
      }))
      setSourceType('')
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Procurement', path: '/procurement' },
    { label: 'Vendor Invoices', path: '/procurement/vendor-invoice' },
    { 
      label: isEdit ? 'Edit Invoice' : isView ? 'View Invoice' : 'New Invoice', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (loading && (isEdit || isView)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading invoice...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEdit ? 'Edit Vendor Invoice' : isView ? 'View Vendor Invoice' : 'New Vendor Invoice'}</h1>
          <p>{isEdit ? 'Update vendor invoice' : isView ? 'View vendor invoice details' : 'Create a new vendor invoice'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/procurement/vendor-invoice')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Invoice Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Invoice Number, Supplier Invoice Number, Invoice Date, Due Date */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="invoiceNumber" className="form-label required">
                Invoice Number
              </label>
              <input
                type="text"
                id="invoiceNumber"
                name="invoiceNumber"
                value={isLoadingInvoiceNumber ? 'Generating...' : formData.invoiceNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="supplierInvoiceNumber" className="form-label required">
                Supplier Invoice Number
              </label>
              <input
                type="text"
                id="supplierInvoiceNumber"
                name="supplierInvoiceNumber"
                value={formData.supplierInvoiceNumber}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Enter supplier invoice number"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="invoiceDate" className="form-label required">
                Invoice Date
              </label>
              <input
                type="date"
                id="invoiceDate"
                name="invoiceDate"
                value={formData.invoiceDate}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="dueDate" className="form-label required">
                Due Date
              </label>
              <input
                type="date"
                id="dueDate"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                required
              />
            </div>
          </div>

          {/* Source Document Selection (only for new invoices) */}
          {!isEdit && !isView && (
            <>
              <div className="grid-row-4">
                <div className="form-group">
                  <label htmlFor="sourceType" className="form-label">
                    Create From
                  </label>
                  <select
                    id="sourceType"
                    name="sourceType"
                    value={sourceType}
                    onChange={handleInputChange}
                    className="form-input"
                  >
                    <option value="">Manual Entry</option>
                    <option value="po">Purchase Order</option>
                    <option value="grn">GRN</option>
                  </select>
                </div>

                {sourceType === 'po' && (
                  <div className="form-group">
                    <label htmlFor="sourceDocument" className="form-label">
                      Select Purchase Order
                    </label>
                    <select
                      id="sourceDocument"
                      name="sourceDocument"
                      value={formData.purchaseOrder}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="">Select Purchase Order</option>
                      {purchaseOrders.map(po => (
                        <option key={po._id} value={po._id}>
                          {po.purchaseOrderNumber} - {po.supplierName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {sourceType === 'grn' && (
                  <div className="form-group">
                    <label htmlFor="sourceDocument" className="form-label">
                      Select GRN
                    </label>
                    <select
                      id="sourceDocument"
                      name="sourceDocument"
                      value={formData.goodsReceiptNote}
                      onChange={handleInputChange}
                      className="form-input"
                    >
                      <option value="">Select GRN</option>
                      {grns.map(grn => (
                        <option key={grn._id} value={grn._id}>
                          {grn.grnNumber} - {grn.supplierName}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Supplier Information */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="supplierName" className="form-label required">
                Supplier
              </label>
              <input
                type="text"
                id="supplierName"
                name="supplierName"
                value={formData.supplierName}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="purchaseOrderNumber" className="form-label">
                PO Number
              </label>
              <input
                type="text"
                id="purchaseOrderNumber"
                name="purchaseOrderNumber"
                value={formData.purchaseOrderNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="grnNumber" className="form-label">
                GRN Number
              </label>
              <input
                type="text"
                id="grnNumber"
                name="grnNumber"
                value={formData.grnNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
          </div>

          {/* Invoice Items Section */}
          <div className="form-section-divider">
            <div className="section-header-with-action">
              <div>
                <h2 className="section-title">Invoice Items</h2>
                <p className="section-subtitle">Items and pricing details</p>
              </div>
            </div>
          </div>

          {formData.invoiceItems.map((item, index) => (
            <div key={index} className="expense-item-card-modern">
              <div className="expense-item-header">
                <div className="expense-item-number">
                  <span className="item-number-badge">{index + 1}</span>
                  <span className="item-number-text">Item {index + 1}: {item.productName || item.serviceName || 'N/A'}</span>
                </div>
              </div>

              <div className="grid-row-4">
                <div className="form-group">
                  <label className="form-label">Quantity</label>
                  <input
                    type="number"
                    value={item.quantity || 0}
                    onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                    className="form-input"
                    min="0.01"
                    step="0.01"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unit</label>
                  <input
                    type="text"
                    value={item.unitOfMeasurement || ''}
                    readOnly
                    className="form-input"
                    style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Unit Price</label>
                  <input
                    type="number"
                    value={item.unitPrice || 0}
                    onChange={(e) => handleItemChange(index, 'unitPrice', e.target.value)}
                    className="form-input"
                    min="0"
                    step="0.01"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Discount (%)</label>
                  <input
                    type="number"
                    value={item.discount || 0}
                    onChange={(e) => handleItemChange(index, 'discount', e.target.value)}
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={isView}
                  />
                </div>
              </div>

              <div className="grid-row-4" style={{ marginTop: '10px' }}>
                <div className="form-group">
                  <label className="form-label">Tax Rate (%)</label>
                  <input
                    type="number"
                    value={item.taxRate || 0}
                    onChange={(e) => handleItemChange(index, 'taxRate', e.target.value)}
                    className="form-input"
                    min="0"
                    max="100"
                    step="0.01"
                    disabled={isView}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Total Amount</label>
                  <div className="expense-item-total-display">
                    {item.currency || 'INR'} {(item.totalAmount || 0).toFixed(2)}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {formData.invoiceItems.length === 0 && !isView && (
            <div className="empty-state-card">
              <div className="empty-state-icon">
                <FileText size={48} />
              </div>
              <h3 className="empty-state-title">No invoice items</h3>
              <p className="empty-state-description">Select a Purchase Order or GRN to auto-fill items, or enter manually</p>
            </div>
          )}

          {/* Financial Summary */}
          <div className="section-header"><h3>Financial Summary</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="financialSummary.shippingCharges" className="form-label">
                Shipping Charges
              </label>
              <input
                type="number"
                id="financialSummary.shippingCharges"
                name="financialSummary.shippingCharges"
                value={formData.financialSummary.shippingCharges}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label htmlFor="financialSummary.otherCharges" className="form-label">
                Other Charges
              </label>
              <input
                type="number"
                id="financialSummary.otherCharges"
                name="financialSummary.otherCharges"
                value={formData.financialSummary.otherCharges}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                min="0"
                step="0.01"
              />
            </div>

            <div className="form-group">
              <label className="form-label">Subtotal</label>
              <div className="expense-item-total-display">
                {formData.financialSummary.currency || 'INR'} {(formData.financialSummary.subtotal || 0).toFixed(2)}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Total Amount</label>
              <div className="expense-item-total-display" style={{ fontSize: '18px', fontWeight: '700', color: '#222448' }}>
                {formData.financialSummary.currency || 'INR'} {(formData.financialSummary.totalAmount || 0).toFixed(2)}
              </div>
            </div>
          </div>

          {/* Tax Information */}
          <div className="section-header"><h3>Tax Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="taxInformation.supplyType" className="form-label">
                Supply Type
              </label>
              <select
                id="taxInformation.supplyType"
                name="taxInformation.supplyType"
                value={formData.taxInformation.supplyType}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
              >
                <option value="Intrastate">Intrastate</option>
                <option value="Interstate">Interstate</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="taxInformation.placeOfSupply" className="form-label">
                Place of Supply
              </label>
              <input
                type="text"
                id="taxInformation.placeOfSupply"
                name="taxInformation.placeOfSupply"
                value={formData.taxInformation.placeOfSupply}
                onChange={handleInputChange}
                disabled={isView}
                className="form-input"
                placeholder="Place of supply"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isView}
              className="form-input"
              rows="3"
              placeholder="Enter any additional notes"
            />
          </div>

          {/* Action Buttons */}
          {!isView && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleReset}
                className="btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary"
                disabled={loading}
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: loading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default VendorInvoiceForm

